// ═══════════════════════════════════════════════════════════════════════════
//  Synqus — Design Tokens
//  Single source of truth for all spacing, typography, radii, shadows,
//  z-index, and animation values used across the UI.
// ═══════════════════════════════════════════════════════════════════════════

import type { CSSProperties } from "react";

// ─── Spacing Scale ──────────────────────────────────────────────────────
// Based on a 4px base with practical half-steps for tight UI.

export const space = {
  0: 0,
  px: 1,
  0.5: 2,
  0.75: 3,
  1: 4,
  1.5: 6,
  2: 8,
  2.5: 10,
  3: 12,
  3.5: 14,
  4: 16,
  4.5: 18,
  5: 20,
  6: 24,
  /** Alias tokens for semantic sizing */
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
} as const;

// ─── Typography ─────────────────────────────────────────────────────────

export const fontSize = {
  /** 8px — micro labels, AI badges */
  "2xs": 8,
  /** 9px — secondary labels, timestamps, tag text */
  xs: 9,
  /** 10px — chip text, small buttons, prep card body */
  sm: 10,
  /** 11px — button text, chat labels, input text */
  md: 11,
  /** 12px — default body, chat messages */
  base: 12,
  /** 13px — section headings, chat header title */
  lg: 13,
  /** 14px — icons, medium headings */
  xl: 14,
  /** 16px — view sub-headings */
  "2xl": 16,
  /** 18px — view headings */
  "3xl": 18,
  /** 20px — page headings */
  "4xl": 20,
  /** 24px — large headings */
  "5xl": 24,
  /** 32px+ — hero display headings */
  hero: 32,
} as const;

export const fontWeight = {
  normal: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
  extrabold: 800,
  black: 900,
} as const;

export const lineHeight = {
  none: 1,
  tight: 1.2,
  snug: 1.4,
  normal: 1.5,
  relaxed: 1.6,
} as const;

// ─── Border Radius ──────────────────────────────────────────────────────

export const radius = {
  /** 3px — tiny badges */
  xs: 3,
  /** 4px — tags, status chips */
  sm: 4,
  /** 6px — people chips, info boxes */
  md: 6,
  /** 8px — buttons, inputs, cards */
  lg: 8,
  /** 10px — chat input */
  xl: 10,
  /** 12px — AI cards, modals */
  "2xl": 12,
  /** 14px — chat bubbles */
  "3xl": 14,
  /** 20px — panels, chat container */
  "4xl": 20,
  /** Circular */
  full: "50%",
} as const;

// ─── Shadows ────────────────────────────────────────────────────────────

export const shadow = {
  /** Subtle outline glow */
  xs: "0 0 0 1px rgba(124,58,237,0.25)",
  /** Light elevation */
  sm: "0 4px 16px rgba(124,58,237,0.2)",
  /** Primary button glow */
  md: "0 8px 32px rgba(124,58,237,0.4)",
  /** Panel/modal shadow */
  lg: "0 20px 60px rgba(0,0,0,0.4)",
  /** Deep page-level shadow */
  xl: "0 32px 80px rgba(0,0,0,0.5)",
} as const;

// ─── Z-Index Layers ─────────────────────────────────────────────────────

export const zIndex = {
  base: 0,
  elevated: 10,
  dropdown: 100,
  overlay: 1000,
  toast: 1100,
} as const;

// ─── Transitions ────────────────────────────────────────────────────────

export const transition = {
  fast: "all 0.15s",
  normal: "all 0.2s",
  slow: "all 0.3s ease",
  bgFast: "background 0.15s",
  /** Alias — same as normal */
  default: "all 0.2s",
} as const;

// ─── Hardcoded Semantic Colors (not theme-dependent) ────────────────────
// These supplement the theme palette for specific UI purposes.

export const semanticColor = {
  white: "#fff",
  /** Success green — available slots, active indicators */
  success: "#10b981",
  /** Warning amber — tentative, caution */
  warning: "#f59e0b",
  /** Error red — unavailable, danger */
  error: "#ef4444",
  /** AI purple — secondary accent for AI features */
  aiPurple: "#8b5cf6",
  /** Neutral gray — disabled, inactive */
  neutral: "#6b7280",
  /** Teal — time hints, supplementary */
  teal: "#06b6d4",
} as const;

// ─── Component Token Presets ────────────────────────────────────────────
// Pre-built style objects for the most common patterns. Components import
// these and spread them, then override only what differs.

export const chatBubble = {
  /** User message bubble corners */
  userRadius: "14px 14px 4px 14px",
  /** Bot message bubble corners */
  botRadius: "14px 14px 14px 4px",
  /** Max width of message bubbles */
  maxWidth: "85%",
  /** Panel width */
  panelWidth: 400,
  /** Settings panel width */
  settingsWidth: 380,
  /** Max height of message area */
  messagesMaxHeight: 400,
  /** Collapsed button size */
  fabSize: 56,
} as const;

// ─── Reusable Inline Style Presets ──────────────────────────────────────

/** Standard interactive chip / tag button */
export const chipStyle: CSSProperties = {
  cursor: "pointer",
  fontFamily: "inherit",
  fontSize: fontSize.sm,
  fontWeight: fontWeight.semibold,
  display: "flex",
  alignItems: "center",
  transition: transition.fast,
};

/** Flex row with wrapping — used for chip/button groups */
export const chipRow: CSSProperties = {
  display: "flex",
  gap: space[1],
  flexWrap: "wrap",
};

/** Status tag / badge — e.g., category, person count, duration */
export const tagStyle: CSSProperties = {
  fontSize: fontSize["2xs"] + 1, // 9px
  padding: "2px 8px",
  borderRadius: radius.sm,
  fontWeight: fontWeight.semibold,
};

/** Standard action button in chat */
export const chatBtnBase: CSSProperties = {
  padding: "8px 12px",
  borderRadius: radius.lg,
  cursor: "pointer",
  fontFamily: "inherit",
  fontSize: fontSize.md,
  fontWeight: fontWeight.bold,
  transition: transition.fast,
  border: "none",
};

/** AI card container */
export const aiCardBase: CSSProperties = {
  marginTop: space[2],
  borderRadius: radius["2xl"],
  overflow: "hidden",
};

/** AI card header row */
export const aiCardHeader: CSSProperties = {
  padding: "8px 12px",
  fontSize: fontSize.sm,
  fontWeight: fontWeight.bold,
  display: "flex",
  alignItems: "center",
  gap: space[1.5],
};

/** AI card body content */
export const aiCardBody: CSSProperties = {
  padding: "10px 12px",
  fontSize: fontSize.md,
  lineHeight: lineHeight.relaxed,
};

/** Circular icon button (header close / settings) */
export const iconBtnStyle: CSSProperties = {
  width: 28,
  height: 28,
  borderRadius: radius.full,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  border: "none",
};
